import React, { useContext, useEffect, useState } from "react";
import "./app.sass";
import { LoadingOutlined } from '@ant-design/icons';
import BasePublic from "../baseLayouts/public/index";
import BasePrivate from "../baseLayouts/private/index";
import { ConfigContext } from "../../hooks/context/config";
import { LoginContext, UserContext, UserContextV2 } from "../../hooks/context/userInfo";
import {
  LangContext,
  AllLanguages,
  LangUrlContext,
} from "../../hooks/context/lang";
import { BrowserRouter as Router } from "react-router-dom";
import LocalizedRouter from "../../routes/public/routes";
import { AppLanguage } from "../../routes/appLanguages";
import { appStrings } from "../../routes/localizations/index";
import LocalizedRouterPrivate from "../../routes/private/routes";
import { CookiesProvider } from "react-cookie";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { DURATION_COOKIE } from "../../constants/constants";
import { RestApiClientContext } from "../../hooks/context/pageContext";
import { RestApiClient } from "../../api/restClient";
import { User } from "../../models/user/userModel";
import { useConfig } from "../../api/config";
import { initLanguages, allLanguages } from "../../utils/languages";
import { useGetUserProfile } from "src/api/api";
import { Spin } from "antd";

// Componente principal de nuestra aplicación y que renderizamos en el main.tsx
export const App = (config) => {
  // Control adicional si esta o no logado el usuario
  const loginUserDone = false;
  // Libreria para el manejador de idioma de la app
  const { t, i18n } = useTranslation();
  // Contexto con el idioma de usuario y en la que se visualizara la app
  const language = useContext(LangContext);
  const languageUrl = useContext(LangUrlContext);
  const allLang = useContext(AllLanguages);
  const userContext = useContext(UserContext);
  const configuration = useContext(ConfigContext);
  const userContextV2 = useContext(UserContextV2);
  const { setUser } = userContextV2;
  // Carga inicial con el color destacado del cliente
  const style =
    configuration &&
    `
    :root {
      --primary-color: ${configuration.colour};
    }
  `;

  const [userLogin, setUserToken, removeCookie] = useCookies(["userToken"]);
  const token = userLogin.userToken;

  const [isAppReady, setIsAppReady] = useState(false);
  const [login, setLogin] = useState(loginUserDone);

  const getUserProfile = useGetUserProfile();
  const initLangURL: string = config.allLanguages.filter(
    (f) => f.id === initLang
  ).shortId;
  let initLang: string = "";

  if (language.GetLangContext() === "") {
    initLang = config.userLang;
  } else {
    initLang = language.GetLangContext();
  }

  useEffect(() => {
    i18n.changeLanguage(initLang);
    allLang.UpdateAllLangContext(config.allLanguages);
    language.UpdateLangContext(initLang);
    languageUrl.UpdateUrlLangContext(initLangURL);
    configuration.UpdateConfigContext(config.config);
  }, [initLang, allLanguages, config]);

  // Actualizar la libreria i18n para el idioma de la web
  useEffect(() => {
    const checkUserLogin = async () => {
      if (token && token !== "null" && !userContext.GetNameUser()) {
        try {
          const response = await getUserProfile(token);
          const info = response.data;

          const expireDate = new Date(Date.now() + DURATION_COOKIE);
          setUserToken("userToken", token, { path: "/", expires: expireDate });
          sessionStorage.setItem("token", token);
          setUser({
            id: info.id,
            idAgentType: info.idAgentType,
            userName: info.userName,
            urlLanguage: info.language.shortId,
            language: info.language.id,
            currency: info.idCurrency,
            imageName: info.imageName,
            email: info.email,
            uaid: info.uaid,
            countryCode: info.idCountry,
            referralCode: info.referralCode,
            receiveCommercialInformationDate: info.receiveCommercialInformationDate !== null,
            token: "",
            totalAvailable: 0,
            idAgency: 0,
            checkAnonymous: info.checkAnonymous,
          })
          userContext.UpdateUserContext({
            id: info.id,
            idAgentType: info.idAgentType,
            userName: info.userName,
            urlLanguage: info.language.shortId,
            language: info.language.id,
            currency: info.idCurrency,
            imageName: info.imageName,
            email: info.email,
            uaid: info.uaid,
            countryCode: info.idCountry,
            referralCode: info.referralCode,
            receiveCommercialInformationDate: info.receiveCommercialInformationDate !== null,
          } as User);

          setLogin(true);
        } catch (error) {
          // Si falla la validación, limpiar y mostrar pública
          sessionStorage.removeItem("token");
          removeCookie("userToken", { path: "/" });
          setLogin(false);
        } finally {
          setIsAppReady(true);
        }
      } else {
        // Sin token o ya cargado
        setIsAppReady(true);
      }
    };

    checkUserLogin();
  }, []);

  if (!isAppReady) return <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Spin tip="Loading..." indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
  </div>;

  return (
    // En el fallback podemos añadir un spinner (<Spinner/>)
    <>
      <LoginContext.Provider
        value={{
          login,
          updateValue: (key) => {
            setLogin(key);
          },
        }}
      >
        <CookiesProvider>
          {login ||
            (typeof userLogin.userToken !== "undefined" &&
              userLogin.userToken !== "null") ? (
            <RestApiClientContext.Provider
              value={new RestApiClient(userLogin.userToken, t)}
            >
              <LocalizedRouterPrivate
                RouterComponent={Router}
                languages={AppLanguage}
                appStrings={appStrings}
                defaultLanguage={initLangURL}
              >
                <style>{style}</style>
                <BasePrivate />
              </LocalizedRouterPrivate>
            </RestApiClientContext.Provider>
          ) : (
            <LocalizedRouter
              RouterComponent={Router}
              languages={AppLanguage}
              appStrings={appStrings}
              defaultLanguage={initLangURL}
            >
              <style>{style}</style>
              <BasePublic />
            </LocalizedRouter>
          )}
        </CookiesProvider>
      </LoginContext.Provider>
    </>
  );
};

const AppWithConfig = () => {
  // Se obtiene la configuracion del cliente
  const config: any = {
    config: useConfig(),
    userLang: initLanguages(),
    allLanguages: allLanguages(),
  };
  return <App {...config} />;
};

export default AppWithConfig;
